import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAsync } from 'react-use';
import * as businessService from '../../data-layer/business/service';
import { BusinessStatsModel } from '../../data-layer/business/types';
import { BusinessStatisticDisclaimer } from '../../ui/business/BusinessStatisticDisclaimer/BusinessStatisticDisclaimer';
import { BusinessStatisticSummary as BusinessStatisticSummaryUI } from '../../ui/business/BusinessStatisticSummary/BusinessStatisticSummary';
import { BusinessStatisticSkeleton } from '../../ui/business/BusinessStatisticSummary/parts/BusinessStatisticSkeleton/BusinessStatisticSkeleton';
import { Flex } from '../../ui/layout/Flex';
import { FeedManagementDateDropdown } from '../../ui/posts/FeedManagement/parts/FeedManagementDateDropdown/FeedManagementDateDropdown';
import { hasAccessToBusinessStat, isBloggerBusiness } from '../../utils/accessService';
import { useExperimentFlag } from '../../utils/useExperimentFlag';
import { useCurrentUser } from '../users/useCurrentUser';
import { useBusinessModel } from './useBusinessModel';

const DEFAULT_STAT_PERIOD = '7';

interface BusinessStatisticProps {
    stats: BusinessStatsModel;
}

export const BusinessStatisticSummary = ({
    businessId: businessIdBase = '',
}: {
    businessId?: string;
}) => {
    const { businessId = businessIdBase } = useParams();
    const business = useBusinessModel({ businessId });
    const isBlogger = isBloggerBusiness(business?.type);
    const [period, setPeriod] = useState<string | null>(DEFAULT_STAT_PERIOD);
    const [dateFrom, setDateFrom] = useState<string | null>(null);
    const [dateTo, setDateTo] = useState<string | null>(null);
    const [businessStatistic, setBusinessStatistic] = useState<BusinessStatisticProps | undefined>(
        undefined,
    );
    const currentUser = useCurrentUser();
    const { loading: statisticLoading } = useAsync(async () => {
        setBusinessStatistic(undefined);
        if (businessId && hasAccessToBusinessStat(businessId, currentUser)) {
            setBusinessStatistic(
                await businessService.loadBusinessStatistic(
                    businessId,
                    period === 'undefined' ? undefined : period,
                    dateFrom,
                    dateTo,
                ),
            );
        }
    }, [businessId, period, dateFrom, dateTo]);

    const onChangeDate = useCallback(
        (value: string | null, dateFromValue?: string, dateToValue?: string) => {
            setPeriod(value);
            setDateFrom(dateFromValue || null);
            setDateTo(dateToValue || null);
        },
        [],
    );

    const enableStatDisclaimer = useExperimentFlag('enable_statistic_disclaimer');

    return (
        <Flex
            borderRadius={2}
            background="var(--background-primary)"
            width="100%"
            overflow="hidden"
            mb={3}
            maxWidth={{ xs: '100%', sm: '90%' }}
        >
            {enableStatDisclaimer ? (
                <Flex p={2} width="100%" borderBottom="1px solid var(--divider)">
                    <BusinessStatisticDisclaimer />
                </Flex>
            ) : null}
            <Flex direction="row" width="100%" p={2} alignItems="center" zIndex={2}>
                <FeedManagementDateDropdown
                    value={period}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onChange={onChangeDate}
                />
            </Flex>
            {statisticLoading ? (
                <BusinessStatisticSkeleton />
            ) : (
                businessStatistic?.stats && (
                    <BusinessStatisticSummaryUI
                        statistic={businessStatistic.stats}
                        borderTop="1px solid var(--divider)"
                        hideDetails={isBlogger}
                    />
                )
            )}
        </Flex>
    );
};
